import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { getIndustry, industries } from '../data/industries';
import { SectionLink } from '../components/SectionLink';
export function IndustryDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const industry = getIndustry(slug);
  useEffect(() => {
    window.scrollTo({
      top: 0
    });
  }, [slug]);
  if (!industry) {
    return <main className="mx-auto flex max-w-shell flex-col items-start px-6 py-28 lg:px-10" data-id="element-181" data-mp="3sblgfjvxr67o">
        <h1 className="font-display text-3xl font-semibold text-navy-900" data-id="element-182" data-mp="14r2ilg4u796">
          Profile not found
        </h1>
        <p className="mt-4 text-base text-muted" data-id="element-183" data-mp="b13v1fzxyf2c">
          That industry profile doesn&apos;t exist. Return to the experience directory.
        </p>
        <Link to="/" className="mt-8 inline-flex items-center gap-2 border border-navy-900 px-5 py-3 text-sm font-semibold text-navy-900 transition-colors duration-150 ease-exec hover:bg-navy-900 hover:text-white" data-id="element-184" data-mp="1hgeb4snbya6p">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-185" data-mp="3vgh4ns2fsnpa" />
          Back to home
        </Link>
      </main>;
  }
  const others = industries.filter(item => item.slug !== industry.slug);
  return <main data-id="element-186" data-mp="3mg2h0m202wim">
      <section className="w-full bg-navy-900 py-16 text-white lg:py-20" data-id="element-187" data-mp="3mgc15t0d69ge">
        <div className="mx-auto max-w-shell px-6 lg:px-10" data-id="element-188" data-mp="3hgpjn7di671s">
          <SectionLink id="experience" className="inline-flex items-center gap-2 text-sm font-medium text-white/60 transition-colors duration-150 ease-exec hover:text-white" data-id="element-189" data-mp="3hiadsvvwnnz4">
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-190" data-mp="37r60s1psid6x" />
            Experience directory
          </SectionLink>
          <div className="mt-8 h-px w-12 bg-gold-500" aria-hidden="true" data-id="element-191" data-mp="2yq86ib55lk1j" />
          <h1 className="mt-6 max-w-3xl font-display text-4xl font-semibold leading-tight tracking-tight sm:text-5xl" data-id="element-192" data-mp="2ypgdn82xy0ey">
            {industry.name}
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-gold-200" data-id="element-193" data-mp="2k2hltc0693zp">
            {industry.summary}
          </p>
        </div>
      </section>

      <section className="w-full bg-white py-20 lg:py-24" data-id="element-194" data-mp="2p09e474qas4f">
        <div className="mx-auto grid max-w-shell grid-cols-1 gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-195" data-mp="2a6mw5wxj68nf">
          <div className="lg:col-span-7" data-id="element-196" data-mp="2f4y585g04f4l">
            <p className="font-display text-2xl leading-snug text-navy-900" data-id="element-197" data-mp="32uafl8n2et8h">
              {industry.focus}
            </p>

            <h2 className="mt-12 text-xs font-semibold uppercase tracking-[0.14em] text-gold-600" data-id="element-198" data-mp="37tdc24ozbr4u">
              Mandate areas
            </h2>
            <ul className="mt-6 divide-y divide-line border-t border-navy-900" data-id="element-199" data-mp="2t24pk4gdilvx">
              {industry.mandates.map(mandate => <li key={mandate} className="py-4 text-[15px] leading-relaxed text-ink" data-id="element-200" data-mp="3lt4qu7nblbjw">
                  {mandate}
                </li>)}
            </ul>
          </div>

          <aside className="lg:col-span-5" data-id="element-201" data-mp="1f4mcb6d8dklt">
            <div className="bg-navy-900 p-8 text-white lg:p-10" data-id="element-202" data-mp="1f3b8x7np6ev8">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gold-500" data-id="element-203" data-mp="1a5t0rjhi83oa">
                Value to the board
              </p>
              <p className="mt-5 text-base leading-relaxed text-white/85" data-id="element-204" data-mp="1otw74tzglhdm">
                {industry.boardValue}
              </p>
              <SectionLink id="contact" className="mt-8 inline-flex items-center gap-2 bg-gold-500 px-5 py-3 text-sm font-semibold text-navy-950 transition-colors duration-150 ease-exec hover:bg-gold-400" data-id="element-205" data-mp="1jxozmwrezxtk">
                Open a board inquiry
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-206" data-mp="1543l1qjc71tv" />
              </SectionLink>
            </div>

            <nav aria-label="Other industry profiles" className="mt-10" data-id="element-207" data-mp="2glbp2aq2mfxi">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted" data-id="element-208" data-mp="2gnoeta22lul4">
                Other functional areas
              </p>
              <ul className="mt-5 divide-y divide-line border-t border-line" data-id="element-209" data-mp="37uyf3qm2pykn">
                {others.map(item => <li key={item.slug} data-id="element-210" data-mp="3bwjmiwfu9kng">
                    <Link to={`/experience/${item.slug}`} className="group flex items-center justify-between gap-4 py-4 text-[15px] font-medium text-navy-900 transition-colors duration-150 ease-exec hover:text-gold-700" data-id="element-211" data-mp="3bxl83uuhdpjf">
                      {item.name}
                      <ArrowRightIcon className="h-4 w-4 flex-none text-gold-600 transition-transform duration-150 ease-exec group-hover:translate-x-0.5" aria-hidden="true" data-id="element-212" data-mp="3uue0lovtqmcp" />
                    </Link>
                  </li>)}
              </ul>
            </nav>
          </aside>
        </div>
      </section>
    </main>;
}